// generatedAppRules.js — THE containment rules for AI-generated workspace apps
// (render_answer_ui views, /add-app extensions, multi-page apps, installed
// /x/<key> apps). Pure data, NO imports: the server's AST check reads this
// same file, so the client and the server refuse the same things.
//
// Nika 2026-10-01: "Cut access now". A generated page runs in the member's
// session. Before this, its `el.call(name)` reached the WHOLE shell function
// registry (getSDK → the member's full SDK), and the only gate was a regex
// list that `el.constructor.constructor('return this')()` and
// `self['par' + 'ent']` walked past.
//
// The model: a generated app may call ONLY the functions named below, may read
// workspace data ONLY as `s.root.<key>` through the seed deny list, and may
// not name anything that reaches a realm, a window, the shell tree, the SDK
// or the router.

// The el.call names a generated app may use — THE one list, identical to the
// server's write-time check (server agents/services/appPageSourceAudit.js
// APP_EL_CALL_ALLOWLIST). Never getSDK or anything returning the sdk, never
// the router, storage, setLang, notifications, a module loader or AI verb, or
// a name from the app's own `functions:` block. Data goes through the
// declarative `fetch:` array (the scoped workspaceProject.* bridge), which
// needs no el.call name.
export const APP_EL_CALL_ALLOWLIST = Object.freeze([
  'formatShortDate',
  'formatMonthShort',
  'formatTime',
  'polyglot'
])

// Member names a generated app may not use on ANY object (a property read, a
// destructured key, a literal computed key). Free identifiers are handled by
// the GENERATED_APP_GLOBALS allowlist below, so window-only names
// (location, history, fetch, …) need no entry here: every path to a window
// object (event.view, ownerDocument, defaultView, contentWindow, …) is itself
// refused. `arguments` is refused as an identifier by the auditor.
export const GENERATED_APP_BLOCKED_MEMBERS = Object.freeze([
  // code from text / the prototype chain
  'constructor',
  'prototype',
  '__proto__',
  'callee',
  'caller',
  // a window or a document from a DOM node
  'ownerDocument',
  'defaultView',
  'contentWindow',
  'contentDocument',
  'parentNode',
  'parentElement',
  'offsetParent',
  'getRootNode',
  'composedPath',
  'assignedSlot',
  'frameElement',
  'ownerElement',
  'closest',
  'querySelector',
  'querySelectorAll',
  'getElementById',
  'getElementsByTagName',
  'getElementsByClassName',
  'insertAdjacentHTML',
  'innerHTML',
  'outerHTML',
  'createElement',
  'appendChild',
  'insertBefore',
  'replaceChild',
  // element methods that turn a string into code or fire a handler
  'setAttribute',
  'setAttributeNS',
  'setAttributeNode',
  'toggleAttribute',
  'setHTMLUnsafe',
  'insertAdjacentElement',
  'insertAdjacentText',
  'attachShadow',
  'click',
  'dispatchEvent',
  'requestSubmit',
  'submit',
  'replaceChildren',
  'replaceWith',
  'showModal',
  // the shell tree, the SDK and the router (an in-shell page is a shell node)
  'parent',
  'node',
  'context',
  'getRoot',
  'getRootState',
  'lookup',
  'lookdown',
  'lookdownAll',
  'sdk',
  'getSDK',
  'router'
])

// Names that start like this are framework internals (el.__ref, …).
export const GENERATED_APP_BLOCKED_PREFIX = '__'

// Calling a function by a name held in a variable defeats the allowlist.
// `.call` is allowed ONLY as `x.call('<allowlisted literal>', …)`; `.apply`
// and `.bind` never.
export const GENERATED_APP_BLOCKED_INDIRECTION = Object.freeze(['apply', 'bind'])

// `s.root.<key>` is the documented data read. In-shell, `s.root` is the LIVE
// workspace root: a key must pass the seed deny list (extensionDataSeed.js —
// the same list an iframe seed uses), and nothing under a `.root` chain may
// write.
export const GENERATED_APP_ROOT_DENY_KEYS = Object.freeze([
  // ONE deny list for the AST gate and the sandbox's state snapshot
  // (ISOLATED-RUNTIME-PLAN §3.4): the member and the workspace's people are
  // never readable by a generated app
  'user',
  'members',
  'jwtClaims',
  'orgs',
  'org',
  'workspaces',
  'workspace',
  'userPrefs',
  'roles',
  'orgMembersByOrgId'
])
export const GENERATED_APP_ROOT_DENY_PATTERN =
  'token|secret|jwt|claim|credential|password|apikey|auth|session'
export const GENERATED_APP_ROOT_WRITE_METHODS = Object.freeze([
  'update',
  'set',
  'replace',
  'reset',
  'add',
  'remove',
  'clean',
  'destroy',
  'toggle',
  'quietUpdate',
  'quietReplace',
  'setByPath',
  'removeByPath',
  'apply',
  'applyReplace',
  'push',
  'pop',
  'shift',
  'unshift',
  'splice',
  'sort',
  'reverse',
  'fill',
  'copyWithin'
])

// Element API members a generated app may still use when the shell's
// ctx.methods are denied by name (in-shell, every ctx.methods entry is a
// property of every element).
export const GENERATED_APP_ELEMENT_MEMBERS = Object.freeze([
  'call',
  'update',
  'set',
  'state',
  'props',
  'text',
  'key',
  'data',
  'value'
])

// String literals with exactly these values are refused anywhere: they are
// the keys a reflective read (getOwnPropertyDescriptor, Object.entries, …)
// needs to reach Function or a window without naming the member.
export const GENERATED_APP_BLOCKED_STRINGS = Object.freeze([
  'constructor',
  'prototype',
  '__proto__',
  'caller',
  'callee',
  'defaultView',
  'ownerDocument',
  'contentWindow',
  'contentDocument',
  'getSDK',
  'globalThis'
])

// Free (global) identifiers a generated app may use. Anything else that is not
// declared in its own scope is refused — window, self, top, frames, opener,
// document, fetch, name, … all fall out by construction.
export const GENERATED_APP_GLOBALS = Object.freeze([
  'undefined',
  'NaN',
  'Infinity',
  'Math',
  'JSON',
  'Date',
  'Intl',
  'Number',
  'String',
  'Boolean',
  'Array',
  'Object',
  'Set',
  'Map',
  'WeakMap',
  'WeakSet',
  'Symbol',
  'Promise',
  'RegExp',
  'Error',
  'TypeError',
  'RangeError',
  'BigInt',
  'parseInt',
  'parseFloat',
  'isNaN',
  'isFinite',
  'encodeURIComponent',
  'decodeURIComponent',
  'encodeURI',
  'decodeURI',
  'console',
  'setTimeout',
  'clearTimeout',
  'setInterval',
  'clearInterval'
])

// The only members of these globals a generated app may read: the reflective
// ones (getPrototypeOf, getOwnPropertyDescriptor, defineProperty, …) reach
// Function without naming it.
export const GENERATED_APP_GLOBAL_MEMBERS = Object.freeze({
  Object: ['keys', 'values', 'entries', 'assign', 'fromEntries', 'freeze', 'isFrozen'],
  Array: ['isArray', 'from', 'of'],
  Promise: ['all', 'allSettled', 'race', 'any', 'resolve', 'reject'],
  console: ['log', 'warn', 'error', 'info', 'debug']
})

// DOMQL lifecycle handlers get (el, s), not an event.
export const GENERATED_APP_LIFECYCLE_HANDLERS = Object.freeze([
  'onInit',
  'onCreate',
  'onRender',
  'onUpdate',
  'onStateUpdate',
  'onBeforeUpdate',
  'onBeforeStateUpdate',
  'onAttachNode',
  'onComplete',
  'onDone',
  'onRemove',
  'onStateCreated',
  'onStateInit',
  'onFrame'
])

// Members a generated app may read off a DOM event handle (`e` in
// `onClick: (e, el, s) => …`). `e.view` is the window; `e.composedPath()`
// ends at it — neither is here.
export const GENERATED_APP_EVENT_MEMBERS = Object.freeze([
  'target',
  'currentTarget',
  'key',
  'code',
  'keyCode',
  'which',
  'shiftKey',
  'ctrlKey',
  'altKey',
  'metaKey',
  'button',
  'buttons',
  'clientX',
  'clientY',
  'pageX',
  'pageY',
  'offsetX',
  'offsetY',
  'deltaX',
  'deltaY',
  'deltaMode',
  'type',
  'detail',
  'repeat',
  'isComposing',
  'inputType',
  'data',
  'timeStamp',
  'defaultPrevented',
  'preventDefault',
  'stopPropagation',
  'stopImmediatePropagation'
])

// Members a generated app may read off `e.target` / `e.currentTarget`.
// `files`, `dataset` and `options` may be read further (File / option fields);
// the global member denylist still applies down the chain.
export const GENERATED_APP_TARGET_MEMBERS = Object.freeze([
  'value',
  'checked',
  'files',
  'name',
  'id',
  'dataset',
  'type',
  'selectionStart',
  'selectionEnd',
  'valueAsNumber',
  'valueAsDate',
  'selected',
  'selectedIndex',
  'options',
  'scrollTop',
  'scrollLeft',
  'scrollHeight',
  'scrollWidth',
  'clientHeight',
  'clientWidth',
  'focus',
  'blur',
  'select',
  'getBoundingClientRect'
])
export const GENERATED_APP_TARGET_DEEP_MEMBERS = Object.freeze(['files', 'dataset', 'options'])

// Below e.target.<deep>: an index (a numeric literal) or these, then the
// item's own members — nothing else, no element methods.
export const GENERATED_APP_TARGET_DEEP_CHAIN = Object.freeze({
  files: {
    list: ['length'],
    item: ['name', 'size', 'type', 'lastModified', 'text', 'arrayBuffer']
  },
  options: {
    list: ['length', 'selectedIndex'],
    item: ['value', 'text', 'label', 'selected', 'index', 'disabled']
  }
  // dataset: exactly one plain key, nothing after it
})

// A handle chain used as a VALUE (an argument, an assignment, a return …)
// must end at one of these primitive members — a node, a list of nodes or
// the event itself never leaves its chain (Object.assign(e.target, …)).
export const GENERATED_APP_EVENT_VALUE_MEMBERS = Object.freeze([
  'key',
  'code',
  'keyCode',
  'which',
  'shiftKey',
  'ctrlKey',
  'altKey',
  'metaKey',
  'button',
  'buttons',
  'clientX',
  'clientY',
  'pageX',
  'pageY',
  'offsetX',
  'offsetY',
  'deltaX',
  'deltaY',
  'deltaMode',
  'type',
  'detail',
  'repeat',
  'isComposing',
  'inputType',
  'data',
  'timeStamp',
  'defaultPrevented'
])
export const GENERATED_APP_TARGET_VALUE_MEMBERS = Object.freeze([
  'value',
  'checked',
  'name',
  'id',
  'type',
  'selectionStart',
  'selectionEnd',
  'valueAsNumber',
  'valueAsDate',
  'selected',
  'selectedIndex',
  'scrollTop',
  'scrollLeft',
  'scrollHeight',
  'scrollWidth',
  'clientHeight',
  'clientWidth',
  'files'
])

// ── DOMQL string props (core-wl W18 corpus) ───────────────────────────────
// A def is DATA the framework turns into DOM: these keys and values would
// put markup or script into the page without any JS the AST could see.

// `tag:` values a generated app may render. No script, iframe, object,
// embed, frame, base, meta, link, style, template, portal, … .
export const GENERATED_APP_TAG_ALLOWLIST = Object.freeze([
  'div',
  'span',
  'p',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'section',
  'article',
  'header',
  'footer',
  'nav',
  'main',
  'aside',
  'figure',
  'figcaption',
  'blockquote',
  'pre',
  'code',
  'strong',
  'em',
  'b',
  'i',
  'u',
  's',
  'small',
  'mark',
  'sub',
  'sup',
  'abbr',
  'time',
  'br',
  'hr',
  'ul',
  'ol',
  'li',
  'dl',
  'dt',
  'dd',
  'table',
  'thead',
  'tbody',
  'tfoot',
  'tr',
  'th',
  'td',
  'caption',
  'colgroup',
  'col',
  'form',
  'fieldset',
  'legend',
  'label',
  'input',
  'textarea',
  'select',
  'option',
  'optgroup',
  'button',
  'output',
  'progress',
  'meter',
  'details',
  'summary',
  'a',
  'img',
  'picture',
  'canvas',
  'video',
  'audio',
  'source',
  'track'
])

// Keys that carry raw markup — refused anywhere in a def (attr: {} included).
export const GENERATED_APP_MARKUP_KEYS = Object.freeze([
  'html',
  'innerHTML',
  'outerHTML',
  'srcdoc',
  'srcDoc',
  'dangerouslySetInnerHTML',
  // a form or a ping posting to another origin
  'action',
  'formAction',
  'formaction',
  'ping'
])

// Keys that take a URL: the value must be FIXED to a safe origin — a literal
// with a safe scheme, or a concatenation / template whose LEFTMOST literal is
// a same-origin path ('/chains/chain?id=' + r.id). A function value, and a
// value whose left end is an expression, stay refused.
export const GENERATED_APP_URL_KEYS = Object.freeze([
  'href',
  'src',
  'poster',
  'xlinkHref',
  'xlink:href',
  'cite',
  'codebase',
  'srcset'
])

// CSS keys whose value may carry url(…): a literal (or a function returning
// only literals); every url(…) inside must be a safe URL.
export const GENERATED_APP_CSS_URL_KEYS = Object.freeze([
  'background',
  'backgroundImage',
  'mask',
  'maskImage',
  'WebkitMaskImage',
  'cursor',
  'content',
  'listStyle',
  'listStyleImage',
  'borderImage',
  'borderImageSource',
  'shapeOutside'
])
export const GENERATED_APP_URL_SCHEMES = Object.freeze(['http', 'https', 'mailto', 'tel'])
// `src` alone may also carry an inline image.
export const GENERATED_APP_SRC_DATA_PREFIX = 'data:image/'

// ── COMPONENTS (generated from evidence by scripts/scan-components.mjs) ───
// A generated app may resolve only these registered components, through
// `extends` / `childExtends` or a bare PascalCase key. The list is the
// framework / smbls plugin atoms and components, the brand design-system
// components and the shared kit components whose definition — read with its
// MODULE closure (the module-level helpers it references, the modules its
// imported bindings come from, and every component it mounts) — passed the
// scan: no SDK, no fetch, no router, no root-state write, no root-state read
// beyond its own UI keys (globalTheme, themeMode, activePage, lang,
// currentPath), no shell overlays / notifications / AI / layout / window
// methods, no ambient scope method (el.scope.<fn> it does not define), no
// frames or app mounts, no window / document / storage, no el.call beyond
// framework utilities + the APP_EL_CALL_ALLOWLIST. Reviewed exceptions:
// Dropdown, Link, RouteLink (core-wl v2). A name with ANY failing definition
// is denied. Off-list names are refused with component_not_allowed:<Name>;
// the app's own `components:` entries are allowed by name when no registered
// component carries it.
export const GENERATED_APP_COMPONENT_ALLOWLIST = Object.freeze([
  'AccessibilityCheck',
  'AccordionItem',
  'AddButton',
  'AddItemButton',
  'AddRemoveButtons',
  'AgentAvatar',
  'AiActionRow',
  'AiMenuHeading',
  'AiMenuPanel',
  'AiOptionRow',
  'AppCapsuleBtn',
  'AppCapsuleBtnDanger',
  'AppCapsuleBtnGhost',
  'AppCapsuleBtnPrimary',
  'AppCapsuleBtnToolbar',
  'AppChoiceChip',
  'AppChoiceChips',
  'AppChoiceGroup',
  'AppColorChip',
  'AppColorChips',
  'AppDialogBody',
  'AppDialogFoot',
  'AppDialogHint',
  'AppDialogTitle',
  'AppField',
  'AppFieldChips',
  'AppFieldColors',
  'AppFieldError',
  'AppFieldHint',
  'AppFieldLabel',
  'AppKeycap',
  'AppLabelStack',
  'AppMain',
  'AppMicroLabel',
  'AppNavLink',
  'AppOption',
  'AppOrgSwitchOverlay',
  'AppPage',
  'AppPageEyebrow',
  'AppPaginationNext',
  'AppPaginationPrevious',
  'AppPaginationStep',
  'AppScrollRow',
  'AppSidebarGroup',
  'AppSidebarRow',
  'AppStatusChip',
  'AppTable',
  'AppTableHead',
  'AppTableNum',
  'AppTableRow',
  'AppToolbar',
  'ArrowButton',
  'ArticleBig',
  'ArticleHeader',
  'ArticleLink',
  'ArticleMedium',
  'ArticleSmall',
  'ArticleTiny',
  'ArticleXS',
  'AssetPicture',
  'AssetsFromInsert',
  'Audio',
  'Avatar',
  'Block',
  'Box',
  'BoxModelAside',
  'BoxSizeItem',
  'Br',
  'BrandA11yBoard',
  'BrandAnimDot',
  'BrandAnimationBoard',
  'BrandAssetBoard',
  'BrandBezierPlot',
  'BrandBorderlessDuo',
  'BrandCode',
  'BrandComponentCell',
  'BrandControlScale',
  'BrandDensityCard',
  'BrandEyebrow',
  'BrandFamilyBoard',
  'BrandFixedSizes',
  'BrandFocusDemo',
  'BrandGradientBoard',
  'BrandHero',
  'BrandIconButtonSpecimen',
  'BrandIconCell',
  'BrandIllustrativeRow',
  'BrandInkRow',
  'BrandLawsList',
  'BrandLocalisationBoard',
  'BrandLogoBoard',
  'BrandMediaBoard',
  'BrandMicroLabel',
  'BrandMotionStep',
  'BrandNote',
  'BrandOverlayAction',
  'BrandOverlayFact',
  'BrandOverlayFrame',
  'BrandOverlayMenuRow',
  'BrandOverlayMotionBoard',
  'BrandOverlayMotionRow',
  'BrandOverlayPage',
  'BrandOverlayPageCard',
  'BrandOverlayPopover',
  'BrandOverlayPopoverFrame',
  'BrandOverlayTrigger',
  'BrandOverlayVeil',
  'BrandPanel',
  'BrandRadiusBoard',
  'BrandRatioChip',
  'BrandRegisterDuo',
  'BrandReservedBoard',
  'BrandResetBoard',
  'BrandSection',
  'BrandShadowBoard',
  'BrandSkinBoard',
  'BrandSkinCard',
  'BrandSkinOption',
  'BrandSkinPill',
  'BrandSkinStage',
  'BrandSkinSwitch',
  'BrandSpacingBars',
  'BrandStateDuo',
  'BrandStatusTag',
  'BrandSubhead',
  'BrandSwatch',
  'BrandThemeBoard',
  'BrandThemeGroupGrid',
  'BrandThemeSpecimen',
  'BrandTimingBoard',
  'BrandTypeRow',
  'BrandTypeScale',
  'BrandVoiceCard',
  'BrandWeightLaw',
  'Button',
  'ButtonSet',
  'ButtonTitle',
  'CancelButton',
  'CanvasButton',
  'CanvasDots',
  'CanvasMobileSidebarPanelChrome',
  'CanvasPanel',
  'CanvasSidebarCard',
  'CanvasSidebarIsland',
  'Caption',
  'CaptionTitle',
  'ChatConfidenceFactor',
  'ChatMarkdown',
  'CheckButton',
  'CheckParagraph',
  'Checkbox',
  'CheckboxField',
  'CheckboxHgroup',
  'ChromeExt',
  'Circle',
  'CircleButton',
  'Clickable',
  'ClickableItem',
  'CloseButton',
  'ColumnParagraphs',
  'ComingSoon',
  'CommonCard',
  'CommonFieldLike',
  'CompactIconButton',
  'ContinueButton',
  'Contributors',
  'ContributorsCopy',
  'Data',
  'DescriptionField',
  'DesignComponent',
  'DesignComponentHeader',
  'DesignComponentProperties',
  'DesignElement',
  'DesignSystem',
  'DesignSystemBanners',
  'Dialog',
  'DialogFooter',
  'DialogHeader',
  'DocsDesignElement',
  'DocsHgroup',
  'DocsLink',
  'DocsSocialLinks',
  'DocsSupportLinks',
  'DotCaption',
  'DotCaptionArticle',
  'DraftSwitcher',
  'DragComponentsTutorial',
  'DragHandler',
  'DragNdropUser',
  'DragParagraph',
  'Dropdown',
  'DropdownArrow',
  'DropdownButton',
  'DropdownCopy',
  'DropdownField',
  'DropdownHeader',
  'DropdownList',
  'DropdownParent',
  'EmptyState',
  'EmptyStateImages',
  'EmptyStateLoading',
  'EmptyStateTiles',
  'ErrorState',
  'ExplanatoryParagraph',
  'Feedback',
  'FileIcon',
  'Flex',
  'Focusable',
  'FocusableComponent',
  'FontsFromInsert',
  'FooteLite',
  'FooterFull',
  'Footnote',
  'Form',
  'GeexBlobPercent',
  'GeexGridBars',
  'GeexRankedStack',
  'GeexSegmentedBar',
  'GeexStackedColumn',
  'GeexTonalBars',
  'GeexValueRules',
  'GetEarlyAccess',
  'GlyphPlateHolder',
  'Grid',
  'GridChooser',
  'Group',
  'GroupField',
  'Gutter',
  'HeaderHeading',
  'Headline',
  'HelloUser',
  'Hgroup',
  'HgroupButton',
  'HgroupRows',
  'Hoverable',
  'Hr',
  'Icon',
  'IconButton',
  'IconCaption',
  'IconGridTypes',
  'IconLinkWithPath',
  'IconNavbar',
  'IconText',
  'ImageParagraph',
  'Img',
  'Inline',
  'InlineFlex',
  'InlineGrid',
  'InlineSelect',
  'Input',
  'InputField',
  'InsertSectionShadow',
  'InstallFooter',
  'InstallHero',
  'InstallableRailSkeleton',
  'InstallableStateBadge',
  'IntegrationItem',
  'IntegrationScopeBadge',
  'IntegrationScopeOption',
  'IntegrationsList',
  'InviteForm',
  'Italic',
  'Item',
  'ItemAlt',
  'ItemHighlight',
  'ItemHighlightAlt',
  'ItemOutline',
  'ItemPreview',
  'KangorooButton',
  'LabelTag',
  'Labeled',
  'LabeledCopy',
  'LabeledHighlight',
  'LabeledLight',
  'LandingFooterLite',
  'LanguageOption',
  'LayerSimple',
  'Li',
  'LibraryItemInLearn',
  'Line',
  'Link',
  'LoaderRatio',
  'LoadingGif',
  'LoadingGifSection',
  'LoadingSpinner',
  'LoginForm',
  'LogoLoader',
  'Lottie',
  'MemberProfileCheckItem',
  'MenuItem',
  'MicroNavbar',
  'ModalBody',
  'ModalFooter',
  'MultipleSelect',
  'NativeSelect',
  'Navbar',
  'NavbarTooltip',
  'NavigationArrows',
  'NavigationDots',
  'Notification',
  'NumbCaption',
  'NumbCaptionArticleBig',
  'NumbCaptionArticleSmall',
  'NumberInput',
  'NumericBulletin',
  'NumericTip',
  'Ol',
  'OrderingItem',
  'PackageFeatureItem',
  'Page',
  'PageBar',
  'PageBarBtn',
  'PageBarMeta',
  'PageBarTitle',
  'PageTemplate',
  'PasswordField',
  'PeopleSection',
  'ProfileGrantCard',
  'ProgressBar',
  'ProjectInfo',
  'ProjectTab',
  'PromptTextarea',
  'QuickStartNumber',
  'QuickTip',
  'Radio',
  'RadioHgroup',
  'Range',
  'RangeField',
  'RangeFieldWithIcon',
  'RangeFieldWithTitle',
  'RangeFieldWithTitleAndIcon',
  'RangeWithButtons',
  'RequestAccessButtons',
  'ResetEmailForm',
  'ResetPasswordsForm',
  'ResizeOverlay',
  'ResponsiveOverlay',
  'RouteLink',
  'Scrollable',
  'Scrollbar',
  'SearchField',
  'SectionAddButton',
  'SectionHeader',
  'SectionHeaderWithNav',
  'SectionTitle',
  'Select',
  'SelectField',
  'SelectRoleField',
  'ServerPreview',
  'SettingsTabs',
  'Shape',
  'SignupForm',
  'SimpleOverlay',
  'SizeUnits',
  'Skeleton',
  'SkeletonCard',
  'SkeletonCircle',
  'SkeletonRow',
  'Span',
  'SquareButton',
  'StateDesignElement',
  'Strong',
  'Subhead',
  'SummaryComponent',
  'Svg',
  'SwitchField',
  'SwitchFieldWithCaption',
  'SwitchInput',
  'TabSet',
  'TabsLink',
  'TabsLinkSwitcher',
  'Tag',
  'Tests',
  'Text',
  'Textarea',
  'TextareaField',
  'TextareaWithButton',
  'Theme',
  'Title',
  'Toggle',
  'ToggleHgroup',
  'ToolTerminal',
  'Tooltip',
  'TooltipHidden',
  'TooltipParent',
  'TutorialBanner',
  'Ul',
  'Underline',
  'UploadAvatar',
  'UploadField',
  'UploadIcon',
  'UploadImage',
  'UploadProgress',
  'UserProfileIntegrationRow',
  'UsersTableCopy',
  'VerticalLine',
  'Video',
  'Vr',
  'WaitlistForm',
  'WiderButton'
])

// Registered names the scan or the tier refused (the server, which cannot see
// the client registry, refuses these by name; the client refuses ANY
// registered name off the allowlist). Reasons: scripts/scan-components.mjs.
export const GENERATED_APP_COMPONENT_DENYLIST = Object.freeze([
  'AccessCodeField',
  'ActionNotification',
  'ActionNotificationButtons',
  'ActiveCanvasUsers',
  'ActiveContentSwitcher',
  'ActiveSyncInPublish',
  'ActivityChart',
  'ActivityChartCell',
  'AddAppCatalogCard',
  'AddPagePickerOverlay',
  'AddReactionEmoji',
  'AddReactionPicker',
  'AddTagStyles',
  'AdmParagraph',
  'AdminActionBtn',
  'AdminActionBtnCompact',
  'AdminActionBtnDanger',
  'AdminActionBtnPrimary',
  'AdminActivityScoringRow',
  'AdminAutoTopup',
  'AdminAutoTopupField',
  'AdminAutoTopupMetric',
  'AdminCard',
  'AdminCardDesc',
  'AdminCardTitle',
  'AdminIconBtn',
  'AdminLedgerRow',
  'AdminLogCalendar',
  'AdminLogCalendarCell',
  'AdminLogDay',
  'AdminLogGroup',
  'AdminLogRow',
  'AdminMetricCard',
  'AdminMetricGrid',
  'AdminPageBody',
  'AdminPageHeader',
  'AdminPlanBenefit',
  'AdminPricingCard',
  'AdminSettingsBody',
  'AdminSidebar',
  'AdminSidebarLink',
  'AdminStatusRow',
  'AdminStatusSection',
  'AdminUsageBreakdownRow',
  'AdminUsageLegend',
  'AdminUsageMetric',
  'AdminUsageRow',
  'AdminUsageSummary',
  'AdminUsageViewButton',
  'AdminUserGroupHeader',
  'AdminUserRow',
  'AgentCard',
  'AgentHeroCard',
  'AgentIdleRow',
  'AgentPulseRail',
  'AgentPulseRow',
  'AiActionConfirmCard',
  'AiBridgeModal',
  'AiContextBar',
  'AiContextChip',
  'AiModePicker',
  'AiModePickerBody',
  'AiModelSwitcher',
  'AiModelSwitcherBody',
  'AiModelSwitcherRow',
  'AiModelSwitcherTrigger',
  'AiPickerRow',
  'AiSettingsButton',
  'AiSettingsPanel',
  'AiWidgetBarChart',
  'AiWidgetBarRow',
  'AiWidgetBlock',
  'AiWidgetChip',
  'AiWidgetChipRow',
  'AiWidgetList',
  'AiWidgetListRow',
  'AiWidgetNumeral',
  'AiWidgetProgressBars',
  'AiWidgetTextBlock',
  'AlertTemplate',
  'AmbientBackdrop',
  'AmbientGround',
  'AnalyticsBarRow',
  'AnalyticsCampaignRow',
  'AnalyticsCampaigns',
  'AnalyticsCard',
  'AnalyticsDailyBand',
  'AnalyticsDailyDayLabel',
  'AnalyticsDemographics',
  'AnalyticsHeader',
  'AnalyticsKpiGrid',
  'AnalyticsKpiTile',
  'AnalyticsMetaChip',
  'AnalyticsNavbar',
  'AnalyticsOnChanges',
  'AnalyticsSparkline',
  'AnalyticsTopPages',
  'AnalyticsTopReferrers',
  'AnalyticsTotals',
  'AnalyticsUsersToday',
  'AnalyticsWeeklyCharts',
  'AnalyticsWhosActive',
  'AnimationTag',
  'AnnouncementCard',
  'AnnouncementDetailModal',
  'AppAssistantDecisionBanner',
  'AppAssistantDecisionCard',
  'AppAssistantThreadPicker',
  'AppAssistantToggle',
  'AppCard',
  'AppCombobox',
  'AppContextMenuButton',
  'AppCreateOrgModal',
  'AppDialog',
  'AppDialogFooter',
  'AppDialogHead',
  'AppFieldInput',
  'AppFieldPrefix',
  'AppFieldSearch',
  'AppFieldSegmented',
  'AppFieldSelect',
  'AppFieldTextarea',
  'AppFrame',
  'AppGlyphPlate',
  'AppGlyphTile',
  'AppGroupColumn',
  'AppHeader',
  'AppHeroCard',
  'AppHueTile',
  'AppInput',
  'AppInputBare',
  'AppInputCompact',
  'AppInputGroup',
  'AppInputPrefix',
  'AppInputWithIcon',
  'AppLightbox',
  'AppListItem',
  'AppNavbar',
  'AppNavbarButton',
  'AppNavbarDropdownItem',
  'AppNavbarMoreMenu',
  'AppOrgSwitcher',
  'AppOrgSwitcherItem',
  'AppOrgSwitcherMenu',
  'AppPagination',
  'AppPersonRow',
  'AppSegmented',
  'AppSegmentedItem',
  'AppSelect',
  'AppSelectCompact',
  'AppSidebar',
  'AppSidebarLink',
  'AppTextarea',
  'AppToolbarMore',
  'AppUsernamePrompt',
  'AppWsGroup',
  'AppWsItem',
  'ApprovalRow',
  'ArpPopoverMenu',
  'ArticleReaderControls',
  'ArticleReaderWord',
  'ArticleView',
  'AssistantAnswerPopover',
  'AssistantBoardCard',
  'AssistantBoardFact',
  'AssistantBoardView',
  'AssistantDeckCard',
  'AssistantDeckPresenter',
  'AssistantDeckSlide',
  'AssistantDeckView',
  'AssistantFormField',
  'AssistantIconButton',
  'AssistantMainLayer',
  'AssistantMainThread',
  'AssistantModeChip',
  'AssistantRecordsBar',
  'AssistantRecordsBlock',
  'AssistantRecordsCell',
  'AssistantRecordsHeadCell',
  'AssistantRecordsHero',
  'AssistantRecordsLine',
  'AssistantRecordsRow',
  'AssistantRecordsSlice',
  'AssistantRecordsTableRow',
  'AssistantSendButton',
  'AssistantSourceCard',
  'AssistantStarter',
  'AssistantStarterList',
  'AssistantThreadBlock',
  'AssistantThreadChips',
  'AssistantThreadRow',
  'AssistantThreadUnavailable',
  'AssistantThreadUrlSync',
  'AttrMachineChip',
  'AttrMemberRow',
  'AuditCard',
  'AvatarBundle',
  'AvatarBundleItem',
  'BaseSizeTool',
  'BirthdayItem',
  'BlockedSection',
  'BlockerChip',
  'BookmarkCard',
  'BookmarkTag',
  'BookmarkTagFilter',
  'BookmarksMain',
  'BookmarksSidebar',
  'BorderRadius',
  'BorderRadiusBox',
  'BoxModel',
  'BoxModelMiddle',
  'BoxModelSubGrid',
  'BoxSize',
  'BoxSizeRow',
  'BrandBackdropList',
  'BrandComponentsGrid',
  'BrandGrayRamp',
  'BrandHueGrid',
  'BrandIconBoard',
  'BrandIconButtonSizes',
  'BrandInkMatrix',
  'BrandOverlayCenter',
  'BrandOverlayCenterSheet',
  'BrandOverlayPhone',
  'BrandOverlayPhoneSheet',
  'BrandOverlaySide',
  'BrandOverlaySidePanel',
  'BrandOverlaySpecimen',
  'BrandPageSelect',
  'BrandRail',
  'BrandRailLink',
  'BrandSiteSearch',
  'BrandSiteShell',
  'BrandThemeToggle',
  'BrandWashBoard',
  'Breadcrumb',
  'BroadcastAudienceSegment',
  'BroadcastComposerAudienceButton',
  'BroadcastComposerModal',
  'BroadcastNewsRow',
  'BroadcastStoryCard',
  'BroadcastTagFilter',
  'CalAddTeammatesModal',
  'CalAgendaDay',
  'CalAgendaEmbed',
  'CalAvailabilityCell',
  'CalAvailabilityDayRow',
  'CalAvailabilityGrid',
  'CalAvailabilityHourHeader',
  'CalConfirmMoveModal',
  'CalDragMoveGhost',
  'CalDragOverlayGhost',
  'CalDragPreview',
  'CalEndRepeatBtn',
  'CalEndRepeatSection',
  'CalEventChip',
  'CalEventDetailModal',
  'CalEventTypeBtn',
  'CalHeader',
  'CalHourLine',
  'CalInviteePill',
  'CalInviteeRow',
  'CalInviteeSuggestion',
  'CalMain',
  'CalMeetingAlertCard',
  'CalMeetingModeBtn',
  'CalMiniCalendar',
  'CalMiniDayCell',
  'CalMiniDayLabel',
  'CalMonthDayCell',
  'CalMonthDayHeader',
  'CalMonthStat',
  'CalMonthView',
  'CalNewBookingModal',
  'CalProfileDetailRow',
  'CalProfileModal',
  'CalProfileTabBtn',
  'CalProfileUnavailRow',
  'CalQuickCreate',
  'CalQuickInvitee',
  'CalQuickTypeBtn',
  'CalRailSheet',
  'CalRecurringBtn',
  'CalRecurringDayBtn',
  'CalSidebar',
  'CalTeamMemberColumn',
  'CalTeamMemberHeader',
  'CalTeamNowColumn',
  'CalTeamNowEvent',
  'CalTeamNowGutterTick',
  'CalTeamNowHeaderCell',
  'CalTeamNowHourLine',
  'CalTeamNowView',
  'CalTeamView',
  'CalTeammateSuggestion',
  'CalTimeLabel',
  'CalUnavailableSlot',
  'CalViewBtn',
  'CalViewSwitcherBtn',
  'CalVisibilityBtn',
  'CalWeekDayColumn',
  'CalWeekDayHeader',
  'CalWeekEvent',
  'CalWeekView',
  'CalibrationOverlay',
  'CallInvitationBanner',
  'CanvasAppHeader',
  'CanvasConnection',
  'CanvasConnectionBar',
  'CanvasCreateIsland',
  'CanvasCreateMenuBody',
  'CanvasCreateMenuItem',
  'CanvasEditor',
  'CanvasEmbed',
  'CanvasEmbed2',
  'CanvasHelpCard',
  'CanvasMainNavbar',
  'CanvasMessage',
  'CanvasNavbar',
  'CanvasOverlay',
  'CanvasOverlayV2',
  'CanvasPagesDropdown',
  'CanvasPrimaryCreateIsland',
  'CanvasProjectSidebar',
  'CanvasPromptTextarea',
  'CanvasSearch',
  'CanvasSearchDropdown',
  'CanvasSearchItem',
  'CanvasSidebarCloseBtn',
  'CanvasSidebarReopenNav',
  'CanvasSplitView',
  'CanvasV2CodeEditor',
  'CanvasVerticalNavbar',
  'CanvasViewNavGroup',
  'CanvasViewNavRow',
  'CanvasViewRail',
  'CanvasViewSidebar',
  'CaseTemplate',
  'CaseTemplateRow',
  'CasesPanelWidget',
  'Chat',
  'ChatAttachmentChip',
  'ChatAttachmentRender',
  'ChatAuthorOption',
  'ChatCard',
  'ChatChannelRow',
  'ChatChannelSettingsModalBody',
  'ChatCode',
  'ChatComposer',
  'ChatConversationGroups',
  'ChatDMPersonRow',
  'ChatDMRow',
  'ChatFieldLabel',
  'ChatFileCard',
  'ChatFilesView',
  'ChatFilterBar',
  'ChatFilterToggle',
  'ChatGroupRow',
  'ChatHeader',
  'ChatHoverActions',
  'ChatHoverDivider',
  'ChatHoverIconBtn',
  'ChatHoverMenuItem',
  'ChatHoverQuickReact',
  'ChatHr',
  'ChatInlineCode',
  'ChatInviteMemberPickRow',
  'ChatLinkRow',
  'ChatLinksView',
  'ChatMain',
  'ChatMemberPickRow',
  'ChatMentionDropdown',
  'ChatMentionRow',
  'ChatMessage',
  'ChatMessageList',
  'ChatMessageRow',
  'ChatMobileRail',
  'ChatMobileSidebarBar',
  'ChatModalKindBtn',
  'ChatNewChannelModalBody',
  'ChatOL',
  'ChatPaneHeader',
  'ChatPaneHeaderBtn',
  'ChatPaneHeaderSubtitle',
  'ChatPaneHeaderTitle',
  'ChatPinnedRow',
  'ChatReactionEmoji',
  'ChatReactionPill',
  'ChatRenameModalBody',
  'ChatReplyBannerItem',
  'ChatSearchPanelBody',
  'ChatSearchResultRow',
  'ChatSettingsMemberRow',
  'ChatSidebar',
  'ChatSidebarGroup',
  'ChatSidebarGroupHeader',
  'ChatSidebarMenuItem',
  'ChatSidebarMore',
  'ChatSourceChip',
  'ChatThreadPanel',
  'ChatThreadReplyRow',
  'ChatTicketRow',
  'ChatTicketsView',
  'ChatToolRow',
  'ChatUL',
  'ChooseCanvas',
  'CityMemberRow',
  'CodeBlock',
  'CodeExample',
  'CodePreview',
  'CodePreviewWidget',
  'CodePreviewWithTitle',
  'CodeWithTitleAndButtons',
  'ColorDropdown',
  'ColorDropdownItem',
  'ColorField',
  'ColorFieldWithTitle',
  'ColorPalleteSection',
  'ColorPicker',
  'ColorPickerInputs',
  'ColorPickerWithTitle',
  'ColorPreview',
  'ColorReferenceTemplate',
  'ColorSelect',
  'ColorSelectWithTitle',
  'ColorShade',
  'ColorSwatchItem',
  'ColorTemplate',
  'ColorTemplateRow',
  'ColorText',
  'Command',
  'CommandBar',
  'CommandPalette',
  'CommentThread',
  'CommentThreadItem',
  'CompanyInfoRow',
  'ComponentAddressBar',
  'ComponentCard',
  'ComponentTag',
  'ConfirmTemplate',
  'ConnectionBadge',
  'ConnectionLabel',
  'ConnectionNavbar',
  'ContentPage',
  'ContentPageSectionLink',
  'ContextDropdown',
  'ContextDropdownItem',
  'CopyButton',
  'CreateAgentModal',
  'CreateMemberModal',
  'CreditBanner',
  'CreditBlocker',
  'CreditWarningPill',
  'CustomTemplate',
  'CvEntityCard',
  'CvEntityCardAction',
  'CvEntityPickerBody',
  'CvEntityPickerRow',
  'CvModeButton',
  'DSGrid',
  'DangerZoneSection',
  'DashboardNavbar',
  'DashboardSidebar',
  'DashboardTabs',
  'DataSourcesInInsert',
  'DayCard',
  'DecisionRow',
  'DecisionsSection',
  'DeckFrame',
  'DemoClaimBanner',
  'DemoClaimBar',
  'DesignSystemItems',
  'DesignSystemLayout',
  'DesignSystemNavbar',
  'DesignSystemOptions',
  'DesignSystemScene',
  'DesignSystemSummary',
  'DesignSystemSummaryMinimal',
  'DesktopFrame',
  'DeviceButtonSet',
  'DirectionOverlay',
  'DocLayout',
  'DocsBrowseColumn',
  'DocsCard',
  'DocsContentHeader',
  'DocsFolderList',
  'DocsFolderRow',
  'DocsGrid',
  'DocsHeader',
  'DocsKbCategoryList',
  'DocsKbCategoryRow',
  'DocsMain',
  'DocsMobileRail',
  'DocsMobileSidebarBar',
  'DocsNotesFilterList',
  'DocsNotesFilterRow',
  'DocsSectionNav',
  'DocsSectionRow',
  'DocsSidebar',
  'DocsSubNav',
  'DocumentRow',
  'DocumentTextStyles',
  'DomainItem',
  'Draggable',
  'DraggableRow',
  'DropOverlay',
  'DropdownItem',
  'DropdownParentFocus',
  'DropdownRoot',
  'DropdownSiblingFocus',
  'DropdownTrigger',
  'DsNavbar',
  'DynamicIsland',
  'DynamicNavbar',
  'EditInNavbarMenu',
  'EditNavbar',
  'EditRequestCard',
  'EditorArea',
  'EditorHeader',
  'EditorMain',
  'EditorPositionNavbar',
  'EditorTools',
  'EmailConfirmationError',
  'EmailConfirmationSuccess',
  'EmptyProjectCard',
  'EnvItem',
  'EnvsNavbar',
  'ExampleItemInLearn',
  'ExamplesInLearn',
  'ExplorerSwitcher',
  'ExportsInPublish',
  'ExtCapChip',
  'ExtFieldPill',
  'ExtSourceRow',
  'ExtendCard',
  'ExtensionCanvasNavbar',
  'FeedDigestSection',
  'FieldDropdown',
  'FieldDropdownGroup',
  'FieldDropdownItem',
  'FileItem',
  'FileSidebarAddNewItem',
  'FileSidebarSearch',
  'FilesCrumb',
  'FilesDesktop',
  'FilesInfoModal',
  'FilesInfoRow',
  'FilesMain',
  'FilesMarquee',
  'FilesNode',
  'FilesNodeFrame',
  'FilesNodeTile',
  'FilesOpenWithLink',
  'FilesScopeSeg',
  'FilesSection',
  'FilesSectionList',
  'FilesSectionTitle',
  'FilesSidebar',
  'FilesSidebarAction',
  'FilesSidebarGettingStarted',
  'FilesSidebarGrid',
  'FilesSidebarGroup',
  'FilesSidebarList',
  'FilesSidebarListItem',
  'FilesSidebarSection',
  'FilesSidebarTitle',
  'FilesToolBtn',
  'FilesTypeFilterRow',
  'FilesUploadLayer',
  'FilesUploadingNode',
  'FilesViewer',
  'FilterChip',
  'FlagAlwaysOnPill',
  'FlagDisclosure',
  'FlagGroupSection',
  'FlagRow',
  'FlagSwitch',
  'FlagsVoiceCard',
  'FnEditNavbar',
  'FontFamilies',
  'FontFamilyObject',
  'FontFamilyRow',
  'FontFamilySelect',
  'FontItem',
  'FontItemRow',
  'FontObject',
  'FontSelect',
  'FontSizeTool',
  'FontWeightObject',
  'GazerNavbar',
  'GeneratedAppBlocked',
  'GenericIntConfigField',
  'GenericIntInstanceCard',
  'GenericIntNewForm',
  'GlobalNotification',
  'GlobalThemeTemplate',
  'GlobalThemeTemplateRow',
  'GlobalThemesSection',
  'GovernanceFields',
  'GradientDropdown',
  'GradientSwatchItem',
  'GradientsSection',
  'GridInDropdown',
  'GridItem',
  'GroupByChip',
  'GroupByControl',
  'HTMLTypeTag',
  'HeaderBreadcrumb',
  'HeaderSearch',
  'HeaderTab',
  'HeaderTabs',
  'HeadingStyles',
  'HeatmapOverlay',
  'HistoryAllTypesRow',
  'HistoryAuthorGroup',
  'HistoryDay',
  'HistoryDensity',
  'HistoryEventCard',
  'HistoryEveryoneRow',
  'HistoryHalf',
  'HistoryHalfCards',
  'HistoryHalfLabel',
  'HistoryNotice',
  'HistoryPersonRow',
  'HistoryRail',
  'HistoryRailCount',
  'HistoryRailIsland',
  'HistoryRailRow',
  'HistoryRailToggle',
  'HistoryStage',
  'HistoryStation',
  'HistoryStepBtn',
  'HistoryTypeRow',
  'HomeAppRail',
  'HomeAppStrip',
  'HomeAppStripAll',
  'HomeAppStripItem',
  'HomeMailRow',
  'HomeNextUpBody',
  'HomePersonalize',
  'HomePersonalizeApp',
  'HomePersonalizeChoice',
  'HomePersonalizeGroup',
  'HomePersonalizeSwitch',
  'HomePosterCaption',
  'HomePosterInk',
  'HomePosterSoftInk',
  'HomeSidebar',
  'HomeSignalChip',
  'HomeSignalGlyph',
  'HomeSignalLink',
  'HomeSignalNone',
  'HomeSignalSkeletonTile',
  'HomeSignalTile',
  'HomeSignalsLine',
  'HomeSkyTile',
  'HomeSummaryBrief',
  'HomeSummaryTiles',
  'HomeTopSummary',
  'HomeWidgetAction',
  'HomeWidgetAvatars',
  'HomeWidgetBody',
  'HomeWidgetColorPicker',
  'HomeWidgetColorSwatch',
  'HomeWidgetDelta',
  'HomeWidgetDot',
  'HomeWidgetDots',
  'HomeWidgetEmpty',
  'HomeWidgetFace',
  'HomeWidgetFooter',
  'HomeWidgetFormHost',
  'HomeWidgetFrame',
  'HomeWidgetHero',
  'HomeWidgetList',
  'HomeWidgetPlate',
  'HomeWidgetProgress',
  'HomeWidgetResizeHandle',
  'HomeWidgetRow',
  'HomeWidgetSpark',
  'IconSection',
  'IconShowcaseItem',
  'IconTemplate',
  'IconTemplateRow',
  'IconTemplateWithTooltip',
  'IconsFromInsert',
  'IconsGroup',
  'IconsGroupWithCaption',
  'IconsPanelWidget',
  'Iframe',
  'ImportInInsert',
  'ImprovementCard',
  'InfraCommandPalette',
  'InfraDemoBanner',
  'InfraDemoCard',
  'InfraDeploymentRow',
  'InfraDetailDrawer',
  'InfraDomainModal',
  'InfraEdge',
  'InfraEnvPicker',
  'InfraFilterRow',
  'InfraLogRow',
  'InfraPaletteRow',
  'InfraRepoModal',
  'InfraSidebar',
  'InfraVarRow',
  'InsertPanel',
  'InsertPanelIcons',
  'InsertPanelLibrary',
  'InstallCdn',
  'InstallDomain',
  'InstallExt',
  'InstallLiveExt',
  'InstallProvider',
  'InstallSymbolsJson',
  'InstallSync',
  'InstallTabs',
  'InstallableGridCard',
  'InstallableListRow',
  'InstallableModalStub',
  'InstallableRail',
  'InstallableRailAddButton',
  'InstallableRailChip',
  'InstallableRailEmpty',
  'InstallableRailHeader',
  'InstallableRailItem',
  'IntegrationCategoryCard',
  'IntegrationCategoryGroup',
  'IntegrationFormField',
  'IntegrationKindRow',
  'IntegrationLogo',
  'IntegrationPickerBody',
  'IntegrationScopeListItem',
  'IntegrationScopeModeBtn',
  'IntegrationScopePicker',
  'IntegrationsInInsert',
  'IntegrationsItem',
  'IntegrationsPanel',
  'InterfaceLanguageField',
  'InterfaceLanguageSelect',
  'IntranetAppHeader',
  'IntroPanel',
  'IntroRun',
  'InviteDropdown',
  'InviteFormInDropdown',
  'IteratorFooter',
  'KbArticleCard',
  'KbArticleGrid',
  'KbHeader',
  'KeyValueColumnFields',
  'KnowledgeBaseMain',
  'LangSwitcher',
  'LangSwitcherItem',
  'LangSwitcherMenu',
  'LanguageSelectField',
  'LaunchOverlay',
  'LearnPanel',
  'LibrariesInLearn',
  'LibrariesRow',
  'LibrariesRowItem',
  'LibraryFrame',
  'LibraryFromInsert',
  'LibraryNavbar',
  'LibraryPickerBody',
  'LibraryPickerItem',
  'LinkedResourcePill',
  'LinkedResources',
  'ListInDropdown',
  'LogItemRow',
  'LoginWindow',
  'Logo',
  'LogoCopy',
  'LogoDropdown',
  'LogsLoadMore',
  'LogsRow',
  'LogsSidebar',
  'MCPConnectorPickerBody',
  'MadeOnSymbols',
  'MailAccountGroup',
  'MailAccountRow',
  'MailAccountSettingsCard',
  'MailAdminAccountRow',
  'MailAdminActionBtn',
  'MailAdminAuditRow',
  'MailAdminConfirmBtn',
  'MailAdminDomainChip',
  'MailAdminProviderCard',
  'MailAdminSelect',
  'MailAdminSelectOption',
  'MailAdminSharedRow',
  'MailAdminStatusBadge',
  'MailAttachmentChip',
  'MailAttachmentPreview',
  'MailAvatar',
  'MailBulkBar',
  'MailComposeDock',
  'MailComposer',
  'MailComposerAttachment',
  'MailComposerToolbar',
  'MailEmptyState',
  'MailFilterBar',
  'MailFolderGroup',
  'MailFolderRow',
  'MailHoverActions',
  'MailInviteCard',
  'MailLabelGroup',
  'MailLabelRow',
  'MailLinkMenu',
  'MailLinkMenuRow',
  'MailMain',
  'MailMessageBody',
  'MailMessageCard',
  'MailMessageHeader',
  'MailMobileRail',
  'MailMobileSidebarBar',
  'MailOverlay',
  'MailProviderButton',
  'MailRecipientChip',
  'MailRecipientOption',
  'MailSchedulePreset',
  'MailSettingsAccountRow',
  'MailSetupAction',
  'MailSetupPanel',
  'MailSetupStep',
  'MailShareChannelRow',
  'MailSidebar',
  'MailSidebarBody',
  'MailSimulatedChip',
  'MailStatusBanner',
  'MailThreadList',
  'MailThreadRow',
  'MailThreadView',
  'MainCanvasNavbar',
  'MainLoader',
  'MainNavbar',
  'MarkdownPreview',
  'MarketplaceAppHeroCard',
  'MarketplaceAppRow',
  'MarketplaceAppStoreView',
  'MarketplaceAuthorChip',
  'MarketplaceBrowseView',
  'MarketplaceCatalogRow',
  'MarketplaceCategoryChip',
  'MarketplaceCopyLinkBtn',
  'MarketplaceCover',
  'MarketplaceDetailGallery',
  'MarketplaceDetailLiveView',
  'MarketplaceDetailMarkdownBody',
  'MarketplaceDetailModal',
  'MarketplaceDetailViewTabs',
  'MarketplaceEmptyState',
  'MarketplaceFeaturedCard',
  'MarketplaceFeaturedToday',
  'MarketplaceFeedComment',
  'MarketplaceFeedComposer',
  'MarketplaceFeedFilterItem',
  'MarketplaceFeedIsland',
  'MarketplaceFeedPost',
  'MarketplaceFeedSidebar',
  'MarketplaceFilterChip',
  'MarketplaceFilters',
  'MarketplaceFollowBtn',
  'MarketplaceFollowSuggestion',
  'MarketplaceInstallButton',
  'MarketplaceInstallCatalogError',
  'MarketplaceInstallConfigField',
  'MarketplaceInstallConfigStep',
  'MarketplaceInstallDepsDialog',
  'MarketplaceInstallModal',
  'MarketplaceInstallScopeStep',
  'MarketplaceIntegrationsSection',
  'MarketplaceIntegrationsView',
  'MarketplaceIsland',
  'MarketplaceKindLink',
  'MarketplaceNavbar',
  'MarketplaceOAuthConnectButton',
  'MarketplaceRailNav',
  'MarketplaceRegistrarDomainPicker',
  'MarketplaceRegistrarSearchResult',
  'MarketplaceResultsBar',
  'MarketplaceSearchField',
  'MarketplaceShortcutsOverlay',
  'MarketplaceShortcutsRow',
  'MarketplaceSigninModal',
  'MarketplaceSurfaceHero',
  'MarketplaceSurfaceTab',
  'MarketplaceSurfaceTabs',
  'MarketplaceTemplateCard',
  'MarketplaceToast',
  'MarketplaceUninstallDependentsDialog',
  'MediaRow',
  'MeetCallWidget',
  'MeetCaptionLine',
  'MeetChatMessage',
  'MeetChatPanel',
  'MeetControlBar',
  'MeetControlBarIconButton',
  'MeetControlBarTextButton',
  'MeetCurrentMemberRow',
  'MeetFeaturedTile',
  'MeetJoinPreviewModal',
  'MeetLiveCaptions',
  'MeetMain',
  'MeetMemberPickRow',
  'MeetNewRoomModal',
  'MeetParticipantTile',
  'MeetRoomList',
  'MeetRoomRow',
  'MeetRoomSettingsModal',
  'MeetSidebar',
  'MeetSuggestionCard',
  'MeetSuggestionConfirmModal',
  'MeetTranscriptPage',
  'MeetTranscriptPanel',
  'MeetTranscriptUtterance',
  'MeetTranscriptUtteranceLine',
  'MeetVideoRoom',
  'MeetWaitingGuestRow',
  'MeetWaitingRoomBanner',
  'MemberProfile',
  'MemberProfileA11yChip',
  'MemberProfileA11yRow',
  'MembersInInvite',
  'MembersNavbar',
  'MembersTable',
  'MenuPanel',
  'MenuPanelItem',
  'Messenger',
  'MessengerOverlay',
  'MinimizedDock',
  'MinimizedDockChip',
  'MobileFrame',
  'MobileRailSheet',
  'MobileTab',
  'Modal',
  'ModalHeader',
  'ModalLayer',
  'ModalRoot',
  'ModalVideo',
  'ModalWindow',
  'ModesInNavbar',
  'MoreInsertMenu',
  'MyTicketCard',
  'NavSearchAppItem',
  'NavSearchCommandItem',
  'NavSearchLoading',
  'NavSearchNoResults',
  'NavSearchResultGroup',
  'NavSearchResultItem',
  'NavTabGlyph',
  'NavbarButton',
  'NavbarButtonSet',
  'NavbarTheming',
  'NavbarTooltipParent',
  'NewProjectPickerBody',
  'NoOrgOverlay',
  'NoteCard',
  'NoteTag',
  'NotesHeader',
  'NotesList',
  'NotesMain',
  'NotificationBell',
  'NotificationPanel',
  'NotificationRow',
  'NotificationWrapper',
  'NpTemplateCard',
  'OfficeTable',
  'OfficeTbody',
  'OfficeTd',
  'OfficeTh',
  'OfficeThead',
  'OfficeTr',
  'OnboardingOverlay',
  'OnboardingPanel',
  'OnlineMember',
  'OnlineMembers',
  'OnlineMembersDock',
  'OptionToggle',
  'OrgChartEditRow',
  'OrgChartModal',
  'OrgChartTreeNode',
  'OrgMemberRow',
  'OrgProjectRow',
  'OrgRow',
  'PageHeader',
  'PageHeaderTitle',
  'PageHero',
  'PageSkeleton',
  'PageSlot',
  'PageSlotBackdrop',
  'PageSlotHeaderBtn',
  'PageSrTitle',
  'Partner',
  'PathnameBreadcrumb',
  'PendingApprovalsSection',
  'PendingInviteCard',
  'PendingInvitesBlock',
  'PeopleFilterLink',
  'PeopleGrid',
  'PeopleGroup',
  'PeopleGroupBlock',
  'PersonCard',
  'PersonDetailModal',
  'PersonPopoverAction',
  'PersonPopoverCard',
  'PersonaPicker',
  'PersonaPill',
  'Picture',
  'PipelineBoard',
  'PipelineCard',
  'PipelineStageColumn',
  'PlatformFrame',
  'PreviewAssistant',
  'PreviewCanvasNavbar',
  'PreviewErrorBoundary',
  'PreviewModesMenu',
  'PreviewNavbar',
  'PreviewSidebar',
  'ProfileAvailabilityCell',
  'ProfileAvailabilityGrid',
  'ProjectCanvasEntityRow',
  'ProjectCanvasEnvPicker',
  'ProjectCanvasFilterRow',
  'ProjectCanvasPageRow',
  'ProjectCanvasSidebar',
  'ProjectCard',
  'ProjectCodeData',
  'ProjectCodeSchema',
  'ProjectCodeSidebar',
  'ProjectPublishModal',
  'ProjectSection',
  'ProjectTileFrame',
  'PromptGalleryCard',
  'PromptGalleryChip',
  'PromptTemplate',
  'ProviderSection',
  'PublishNavbar',
  'PublishTools',
  'QuestionMarkTooltip',
  'QuickAddPanel',
  'QuickEditor',
  'QuickEditorNavbar',
  'RatioTool',
  'ReactionPill',
  'ReadyToTestRail',
  'ReadyToTestRow',
  'ReminderComposer',
  'ReminderGroupSection',
  'ReminderMentionOption',
  'ReminderRow',
  'RemoteRowEditor',
  'RemoteRowField',
  'RemoteRowTable',
  'RemoteRowTr',
  'RemoteSchemaList',
  'RemoteTableCard',
  'RemoteUpdateBannerCard',
  'RequestCard',
  'RequestTag',
  'RequestsAuditMain',
  'RequestsInboundMain',
  'RequestsMain',
  'RequestsOutboundMain',
  'ResolutionDrawer',
  'ResolutionDrawerBackdrop',
  'ResolutionFileChip',
  'ResolutionGroupHeader',
  'ResolutionGroupSection',
  'ResolutionRow',
  'ResolutionsEditRequestsMain',
  'ResolutionsFilterBar',
  'ResolutionsGroupByBtn',
  'ResolutionsGroupByToggle',
  'ResolutionsGroupedList',
  'ResolutionsImprovementsMain',
  'ResolutionsSidebar',
  'ResponsiveSwitching',
  'RoleManagerRow',
  'RoleSelect',
  'RouterLink',
  'SamHeaderGhostBtn',
  'SaveCheckpointControl',
  'Scene',
  'SceneNavbar',
  'SceneTemplate',
  'SearchAppTile',
  'SearchAppsMenu',
  'SearchAppsSection',
  'SearchBar',
  'SearchBtn',
  'SectionBlock',
  'SelectDropdown',
  'SelectDropdownWithTitle',
  'SelectedArticlesPanel',
  'SelectedLayerNavbar',
  'SelectedNavbar',
  'SemanticColorItem',
  'SemanticColorTemplate',
  'SemanticColorTemplateRow',
  'SemanticColorsSection',
  'SequenceSliders',
  'ServiceNode',
  'SessionMenu',
  'SessionOverlayActions',
  'SettingRow',
  'SettingsDrawer',
  'SettingsFooter',
  'SettingsModalBody',
  'SettingsSection',
  'ShareLinkModal',
  'ShareLinkRow',
  'SharedViewSwitcher',
  'SharedViewSwitcherBtn',
  'SharingNavbar',
  'SharingPermissions',
  'ShortcutsOverlay',
  'ShortcutsSheetGroup',
  'ShortcutsSheetJoiner',
  'ShortcutsSheetRow',
  'ShowcaseBanner',
  'ShowcaseGrid',
  'SidebarAgentRow',
  'SidebarChatRow',
  'SidebarEventRow',
  'SidebarItemMenuItem',
  'SidebarItemMore',
  'SidebarPricingOverlay',
  'SidebarRow',
  'SidebarSignupOverlay',
  'SimoneReasoningPill',
  'SimoneSuggestions',
  'SkeletonPill',
  'SlidersResponsive',
  'SlidersTestText',
  'StandupActionCard',
  'StandupAppliedCard',
  'StandupDayCard',
  'StandupFollowUp',
  'StandupHistoryAction',
  'StandupQuickFill',
  'StandupSummaryRow',
  'StateEditNavbar',
  'StoriesSection',
  'StoryCard',
  'StoryCardSegment',
  'StudioNavbar',
  'StudioTypeFilterRow',
  'SubFlagRow',
  'SuggestionAction',
  'SyncSuggestionsInPublish',
  'SystemStatusLegendItem',
  'TVFrame',
  'TemplatesRow',
  'TemplatesRowItem',
  'Terminal',
  'TerminalApp',
  'TerminalCaret',
  'TerminalCaretIdle',
  'TerminalChromeButton',
  'TerminalEmpty',
  'TerminalPane',
  'TerminalRow',
  'TerminalSpan',
  'TerminalStage',
  'TerminalTab',
  'TerminalTabBar',
  'TerminalTitleBar',
  'TerminalWindow',
  'TerminalWithTitle',
  'TestOverlay',
  'TestText',
  'TextNavbar',
  'TextNavbarButton',
  'TextNavbarButtonDropdown',
  'TextTagStyleRow',
  'ThemeButtons',
  'ThemeCard',
  'ThemeDropdown',
  'ThemeDropdownItem',
  'ThemeField',
  'ThemeSelect',
  'ThemeTemplate',
  'ThemeTemplateRow',
  'ThemeTemplateWithValues',
  'ThemeToggle',
  'ThemesInNavbar',
  'ThemesSection',
  'TicketAttachmentRow',
  'TicketCardGrid',
  'TicketCommentRow',
  'TicketDependenciesPanel',
  'TicketDependencyDraftRow',
  'TicketDependencyMainRow',
  'TicketDependencyRow',
  'TicketMentionModal',
  'TicketsActivityHeatmap',
  'TicketsActivityRow',
  'TicketsAnswerColumn',
  'TicketsAnswerView',
  'TicketsAssigneeAvatar',
  'TicketsAssigneeGroup',
  'TicketsCard',
  'TicketsDailyUpdate',
  'TicketsDetailAssignee',
  'TicketsDetailField',
  'TicketsDetailModal',
  'TicketsDetailPersonSuggestion',
  'TicketsDraftsGroup',
  'TicketsDraftsToggle',
  'TicketsEpicCard',
  'TicketsEpicGroup',
  'TicketsEpicHeroStat',
  'TicketsEpicManageRow',
  'TicketsEpicPicker',
  'TicketsEpicsMain',
  'TicketsEpicsManageModal',
  'TicketsEpicsStoriesMain',
  'TicketsFeedMain',
  'TicketsFilterBtn',
  'TicketsFilterGroups',
  'TicketsGeneralMain',
  'TicketsHeader',
  'TicketsHeaderFilter',
  'TicketsHeatmapCell',
  'TicketsIssuerGroup',
  'TicketsKanban',
  'TicketsKanbanColumn',
  'TicketsLabelChip',
  'TicketsLogsMain',
  'TicketsMain',
  'TicketsMyViewMain',
  'TicketsReleaseHistory',
  'TicketsReleaseMain',
  'TicketsReleaseRepoChip',
  'TicketsReleaseRow',
  'TicketsReleaseShowMoreBtn',
  'TicketsReleaseStageStaging',
  'TicketsReleaseTicketRow',
  'TicketsReleaseVersionCell',
  'TicketsReleaseVersionsHeader',
  'TicketsReleaseVersionsRow',
  'TicketsReleaseVersionsStage',
  'TicketsResolutionsMain',
  'TicketsRoadmapActionBtn',
  'TicketsRoadmapActionBtnText',
  'TicketsRoadmapAttachTicketModal',
  'TicketsRoadmapCycleCard',
  'TicketsRoadmapCycleEditModal',
  'TicketsRoadmapCycleTicketRow',
  'TicketsRoadmapMain',
  'TicketsSidebar',
  'TicketsSidebarGroup',
  'TicketsSidebarItem',
  'TicketsStandupsMain',
  'TicketsStoriesEpicGroup',
  'TicketsStoriesMain',
  'TicketsStoryCard',
  'TicketsStoryChildRow',
  'TicketsStoryDetailModal',
  'TicketsStoryGroupSection',
  'TicketsTypeBtn',
  'TicketsTypeGroup',
  'TimingPanelWidget',
  'TimingRow',
  'TimingTable',
  'TitleTooltipTrigger',
  'TokenSwatch',
  'ToolCallCard',
  'TooltipRoot',
  'TooltipTrigger',
  'TriageCard',
  'TriageDensityToggle',
  'TriageFlatGroup',
  'TriageGroup',
  'TriageLabelChip',
  'TriageQuickCreate',
  'TriageRetryCTA',
  'TriageSection',
  'TriageSectionSkeleton',
  'TriageSelectionActionBar',
  'TriageSkeleton',
  'TutorialGifNotification',
  'TutorialGifNotificationSlider',
  'TutorialGifsNotificationStack',
  'TutorialItemInLearn',
  'TutorialNotification',
  'TutorialNotificationButtons',
  'TutorialNotificationStack',
  'TutorialPillsNotification',
  'TutorialsInLearn',
  'TutorialsPanel',
  'TutorialsRow',
  'TutorialsRowItem',
  'TypePreview',
  'TypeRow',
  'TypeScale',
  'TypeScaleRow',
  'TypeScaleSequence',
  'TypeSizeTitle',
  'TypeTagsStyles',
  'TypographyPanelWidget',
  'TzAvatarItem',
  'TzCluster',
  'UpcomingEventCard',
  'UploadedFonts',
  'UrlAddressBar',
  'UserProfile',
  'UserProfileFollowRow',
  'UserProfileProjectCard',
  'UserProfileTab',
  'UsernamePromptGate',
  'UsersTable',
  'VersionDetailPanel',
  'VersionDiffModalBody',
  'VersionHistoryPanel',
  'VersionRestoreConfirmBody',
  'VideoWrapper',
  'VirtualSentinel',
  'VoiceControls',
  'VoiceEngineTag',
  'VoiceLog',
  'VoiceOverlay',
  'VoiceStrip',
  'WatchVideo',
  'WhitelabelPersonaRow',
  'WhitelabelRequestAccess',
  'WhitelabelRoleRow',
  'WhitelabelSignin',
  'WhitelabelTurnstile',
  'WidgetRenderer',
  'WorkspaceActivationStatus',
  'WorkspaceCallAction',
  'WorkspaceCallPanel',
  'WorkspaceChip',
  'WorkspaceHubCard',
  'WorkspaceRow',
  'WorkspaceRowButton',
  'WorkspaceSwitcherItem',
  'WorkspaceThumb',
  'WorkspaceUiGrid',
  'WorkspaceUiRail',
  'WorkspaceUiShell',
  'WysiwygToolbar',
  'YouAreConnected',
  'YourAgentCard',
  'ZoomInNavbar',
  'Zoombar'
])
